import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import api from '../services/api.service';

type TAppsPrefWeekNamesState = {
  loading: boolean;
  hasErrors: boolean;
  weekNames: WeekName[];
};

type WeekName = {
  week: string;
};

export const initialState: TAppsPrefWeekNamesState = {
  loading: false,
  hasErrors: false,
  weekNames: [],
};

// Slice
const slice = createSlice({
  name: 'prefWeekNames',
  initialState,
  reducers: {
    fetchData: (state) => {
      isLoadingRequest(state);
    },
    fetchDataSuccess: (
      state,
      { payload }: PayloadAction<TTEPreferencesWeekNames['weekNames']>
    ) => {
      state.weekNames = payload;
      finishedLoadingSuccess(state);
    },
    fetchDataFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

// Selectors
export const weekNamesLoading = (state: TRootState) =>
  state.prefWeekNames.loading;
export const weekNamesSelector = (state: TRootState) =>
  state.prefWeekNames.weekNames;

// Actions
export const { fetchData, fetchDataSuccess, fetchDataFailure } = slice.actions;

export const getWeekNames = createAppAsyncThunk(
  'prefWeekNames/getWeekNames',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchData());
      const { weekNames } = (await api.get({
        endpoint: '/apps/TE_PREFERENCES/week-names',
      })) as TTEPreferencesWeekNames;
      dispatch(fetchDataSuccess(weekNames));
    } catch (e) {
      dispatch(fetchDataFailure());
      return console.error(e);
    }
  }
);

type TTEPreferencesWeekNames = {
  weekNames: WeekName[];
};

export const updateWeekNames = createAppAsyncThunk<
  void,
  TTEPreferencesWeekNames['weekNames']
>('prefWeekNames/updateWeekNames', async (weekNames, { dispatch }) => {
  try {
    await api.patch({
      endpoint: '/apps/TE_PREFERENCES/week-names',
      data: {
        weekNames,
      } satisfies TTEPreferencesWeekNames,
    });
    dispatch(getWeekNames());
  } catch (e) {
    dispatch(fetchDataFailure());
    return console.error(e);
  }
});
